import { useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LoaderCircleIcon, SendIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';

import Error from '../../../feedback/error.jsx';
import Loading from '../../../feedback/loading.jsx';
import {
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../ui/alert-dialog.jsx';
import { Button } from '../../../ui/button.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '../../../ui/form.jsx';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '../../../ui/input-otp.jsx';

export const FormAccountActionValidateOtp = ({
  actionTitle,
  verifyEndpoint,
  sendOtpEndpoint,
  onFormSuccess,
}) => {
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendIntervalRef = useRef(null);

  const formAccountActionValidateOtp = useForm({
    mode: 'onChange',
    defaultValues: {
      otp: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        otp: yup
          .string()
          .min(6, 'Por favor insira o código que recebeu no seu e-mail')
          .required(),
      }),
    ),
  });

  const queryAccountRequestOtp = useQuery({
    queryKey: ['account_action_otp'],
    queryFn: async () => {
      return await msCatalystApi.post(sendOtpEndpoint);
    },
  });

  const mutationAccountResendOtp = useMutation({
    mutationFn: async (data) => {
      await msCatalystApi.post(sendOtpEndpoint);

      return true;
    },
    onSuccess: () => {
      toast.success('Código enviado com sucesso', {
        description: 'Por favor insira o novo código enviado para o seu e-mail',
      });
    },
    onError: (err) => {
      toast.error('Ocorreu um erro ao reenviar o código', {
        description: 'Por favor tente novamente mais tarde',
      });
    },
  });

  const mutationAccountValidateOtp = useMutation({
    mutationFn: async (data) => {
      await msCatalystApi.post(verifyEndpoint, {
        otp: data.otp,
      });

      return true;
    },
    onSuccess: () => {
      onFormSuccess();

      formAccountActionValidateOtp.reset();
    },
    onError: (err) => {
      if (
        err instanceof AxiosError &&
        err.response.data?.message === 'OTP not found or expired.'
      ) {
        toast.error('O código que inseriu não existe ou já expirou', {
          description:
            'Por favor peça um novo código ao carregar no botão "Reenviar Código"',
        });

        return;
      }

      toast.error('Ocorreu um erro ao validar o código', {
        description:
          'Por favor tente novamente mais tarde ou peça um novo código ao carregar no botão "Reenviar código"',
      });
    },
  });

  useEffect(() => {
    if (formAccountActionValidateOtp.isSubmitSuccessful) {
      formAccountActionValidateOtp.reset();
    }
  }, [formAccountActionValidateOtp.formState]);

  // useEffect for the resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      resendIntervalRef.current = setInterval(() => {
        setResendCooldown((prevCooldown) => {
          if (prevCooldown <= 1) {
            clearInterval(resendIntervalRef.current);
            return 0; // Resets cooldown
          }
          return prevCooldown - 1;
        });
      }, 1000);
    } else if (resendIntervalRef.current) {
      // Clear interval if cooldown is set to 0 externally
      clearInterval(resendIntervalRef.current);
    }

    return () => {
      // Cleanup interval on component unmount or if resendCooldown changes
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, [resendCooldown]);

  if (queryAccountRequestOtp.isPending) {
    return <Loading />;
  }

  if (queryAccountRequestOtp.isError) {
    return <Error />;
  }

  return (
    <Form key="validate-otp" {...formAccountActionValidateOtp}>
      <form
        name="validate-otp"
        onSubmit={formAccountActionValidateOtp.handleSubmit((data) => {
          mutationAccountValidateOtp.mutate(data);
        })}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{actionTitle}</AlertDialogTitle>
          <p className="text-sm text-muted-foreground">
            Por motivos de segurança necessitamos que insira o código que foi
            enviado para o seu e-mail
          </p>
        </AlertDialogHeader>

        <div className="py-4">
          <FormField
            control={formAccountActionValidateOtp.control}
            name="otp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Código</FormLabel>
                <FormControl>
                  <InputOTP maxLength={6} {...field}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                    </InputOTPGroup>

                    <InputOTPSeparator />

                    <InputOTPGroup>
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                    </InputOTPGroup>

                    <InputOTPSeparator />

                    <InputOTPGroup>
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>

                <Button
                  type="button"
                  variant="link"
                  className="px-0 pt-1 text-xs text-muted-foreground"
                  disabled={
                    mutationAccountResendOtp.isPending ||
                    mutationAccountValidateOtp.isPending ||
                    resendCooldown > 0
                  }
                  onClick={() => mutationAccountResendOtp.mutate()}
                >
                  {resendCooldown > 0 ? (
                    <LoaderCircleIcon className="animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                  ) : (
                    <SendIcon />
                  )}
                  {resendCooldown > 0
                    ? `Reenviar código em ${resendCooldown}s`
                    : 'Reenviar código'}
                </Button>
              </FormItem>
            )}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={
              mutationAccountValidateOtp.isPending ||
              mutationAccountResendOtp.isPending
            }
            onClick={() => {
              formAccountActionValidateOtp.reset();
            }}
          >
            Cancelar
          </AlertDialogCancel>

          <Button
            type="submit"
            variant="portal"
            disabled={mutationAccountValidateOtp.isPending}
            isLoading={mutationAccountValidateOtp.isPending}
          >
            Confirmar
          </Button>
        </AlertDialogFooter>
      </form>
    </Form>
  );
};
